import { useCallback, useMemo } from "react";
import { useAuth } from "@jana-technology/platform-ui";
import useSWR from "swr";

import { CONFIGURATION_BASE_PATH } from "constants/configuration";
import {
  configurationFetcher,
  generateConfigurationSwrConfig,
} from "contexts/configuration/configuration-swr";
import type { GetAssetCategoryLookupDto } from "contexts/configuration";

import { AppliedConfigContext } from "./context";
import type { AppliedConfigContextValue } from "./types";

const API_BASE_URL = import.meta.env.API_BASE_URL as string;

export const AppliedConfigProvider = ({
  children,
}: React.PropsWithChildren) => {
  const { getTokenSilently } = useAuth();

  const fetchConfigurationWithToken = useCallback(
    async <T,>(requestKey: ReadonlyArray<string>): Promise<T> => {
      const token = await getTokenSilently();

      return configurationFetcher<T>(requestKey, token);
    },
    [getTokenSilently],
  );
  const configurationSwrConfig = useMemo(
    () =>
      generateConfigurationSwrConfig({
        tags: {
          component: "AppliedConfigProvider",
          caller: "fetchConfigurationWithToken",
        },
      }),
    [],
  );

  const attributeMappingResponse = useSWR<Array<GetAssetCategoryLookupDto>>(
    [`${API_BASE_URL}${CONFIGURATION_BASE_PATH}`, "attributes", "mapping"],
    fetchConfigurationWithToken,
    configurationSwrConfig,
  );

  const value = useMemo<AppliedConfigContextValue>(
    () => ({
      attributeMappingResponse,
    }),
    [attributeMappingResponse],
  );

  return (
    <AppliedConfigContext.Provider value={value}>
      {children}
    </AppliedConfigContext.Provider>
  );
};
